import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontMerriweather, fontRoboto, transientOptions } from "@product/scmp-sdk";

import { ArticleAuthorBlockContainer } from "~/components/article/article-author-block/styles";
import { ArticleHeaderAdSlot } from "~/components/article/article-header-ad-slot";
import {
  ArticleImageContainer,
  DescriptionContainer as ArticleImageDescriptionContainer,
} from "~/components/article/article-image/styles";
import { ArticleLandingBanner } from "~/components/article/article-landing-banner";
import { pageStyle } from "~/components/article/article-render/article-content/standard-styles";
import { ContentBodyContainer } from "~/components/content/content-body/styles";
import {
  Heading1,
  Heading2,
  Heading3,
  Heading4,
} from "~/components/content/content-schema-render/heading/styles";
import { StyledParagraph } from "~/components/content/content-schema-render/paragraph/styles";
import { ContentSubHeadlineContainer } from "~/components/content/content-sub-headline/styles";
import { ContentSummaryContainer } from "~/components/content/content-summary/styles";
import type { WithStickyStackedHeightProps } from "~/lib/styles";
import {
  cssVariableValues,
  setFullViewportWidthStyles,
  setFullViewportWidthWithMaxPageWidthStyles,
} from "~/lib/styles";

export const LongReadArticleContainer = styled.div`
  padding-inline: 20px;

  ${setFullViewportWidthStyles()}
`;

export const LongReadArticleWrapper = styled.div`
  display: grid;
  grid:
    "landing-banner landing-banner landing-banner" min-content
    "summary-and-author summary-and-author summary-and-author" min-content
    "header-ad header-ad header-ad" min-content
    "speech-player speech-player speech-player" min-content
    "content content content" min-content
    "content-footer-ad content-footer-ad content-footer-ad" min-content
    "comment comment social" min-content
    "more-on-this more-on-this more-on-this" min-content
    "author-detail author-detail author-detail" min-content
    "topic topic topic" min-content
    "footer-ad footer-ad footer-ad" min-content
    / minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);

  inline-size: 100%;
  max-inline-size: 1456px;
  margin-inline: auto;

  ${props => props.theme.breakpoints.up("tablet")} {
    grid:
      "landing-banner landing-banner landing-banner landing-banner" min-content
      "summary-and-author summary-and-author summary-and-author summary-and-author" min-content
      "left . header-ad header-ad" min-content
      "left . speech-player speech-player" min-content
      "left . content content" min-content
      "left . content-footer-ad content-footer-ad" min-content
      "left . comment social" min-content
      "left . more-on-this more-on-this" min-content
      "left . author-detail author-detail" min-content
      "left . topic topic" min-content
      "left . footer-ad footer-ad" min-content
      "left . . ." min-content
      / 60px 30px minmax(0, 0.6fr) minmax(0, 0.4fr);
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    grid:
      "landing-banner landing-banner landing-banner landing-banner" min-content
      "summary-and-author summary-and-author summary-and-author summary-and-author" min-content
      "header-ad header-ad header-ad header-ad" min-content
      "left speech-player speech-player ." min-content
      "left content content ." min-content
      "left content-footer-ad content-footer-ad ." min-content
      "left comment social ." min-content
      "left more-on-this more-on-this ." min-content
      "left author-detail author-detail ." min-content
      "left topic topic ." min-content
      "left . . ." min-content
      / minmax(0, 1fr) minmax(0, 400px) minmax(0, 280px) minmax(0, 1fr);
  }

  > *:empty {
    margin: 0;
    padding: 0;
  }
`;

export const AppBarScrollOverTracker = styled.div`
  grid-row: landing-banner-start / summary-and-author-end;
  grid-column: span 1;

  inline-size: 0;

  visibility: hidden;
`;

type LandingBannerProps = {
  $addAppBarNegativeMargin: boolean;
};

export const LandingBanner = styled(ArticleLandingBanner, {
  ...transientOptions,
})<LandingBannerProps>`
  grid-area: landing-banner;

  ${props => props.theme.breakpoints.only("mobile")} {
    ${setFullViewportWidthStyles()}
  }

  ${props =>
    props.$addAppBarNegativeMargin
      ? css`
          margin-block-start: calc(${cssVariableValues.appBarHeight} * -1);
        `
      : css`
          margin-block-start: -40px;
        `}
`;

export const Left = styled.div<WithStickyStackedHeightProps>`
  grid-area: left;

  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 40}px;

  display: none;

  transition: inset-block-start 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: flex;
    flex-flow: column nowrap;
    gap: 60px;
    align-items: center;
    align-self: flex-start;

    margin-block-start: 40px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    padding-inline-end: 88px;
  }
`;

export const SummaryAndAuthor = styled.div`
  grid-area: summary-and-author;

  padding-block: 32px;

  background-color: #001246;

  ${props => props.theme.breakpoints.only("mobile")} {
    margin-block-start: calc(${cssVariableValues.appBarHeight} * -1);
    padding-block-start: 28px;
    padding-block-end: 28px;
    padding-inline: 32px;
  }

  ${ArticleAuthorBlockContainer}, ${ContentSubHeadlineContainer}, ${ContentSummaryContainer} {
    inline-size: 100%;
    max-inline-size: 680px;
    margin-inline: auto;
  }

  ${ContentSubHeadlineContainer}, ${ContentSummaryContainer} {
    margin-block-end: 32px;

    color: #ffffff;
    font-weight: 300;
    font-size: 24px;
    font-family: ${fontRoboto};
    line-height: 32px;

    list-style: disc;

    & ul {
      padding: 0;

      list-style: disc;

      li {
        margin-block-end: 12px;
      }
    }

    & li:before {
      display: none;
    }

    ${StyledParagraph} {
      display: list-item;
    }

    ${props => props.theme.breakpoints.only("mobile")} {
      font-size: 20px;
      line-height: 25px;
    }
  }

  ${setFullViewportWidthStyles()}

  ${props => props.theme.breakpoints.only("mobile")} {
    padding-block-start: 28px;
    padding-block-end: 23px;
    padding-inline: 32px;
  }
`;

export const Content = styled.div`
  grid-area: content;

  margin-block-start: 32px;

  ${ContentBodyContainer}:first-child > p:first-child:first-of-type:first-letter {
    /* should not use 'inline-start' since this value not yet supported by many browsers */
    /* https://developer.mozilla.org/en-US/docs/Web/CSS/float */
    float: left; /* stylelint-disable-line liberty/use-logical-spec */

    margin-inline-end: 24px;

    color: #001246;
    font-weight: 900;
    font-size: 90px;
    font-family: ${fontMerriweather};
    line-height: 90px;
    text-shadow: 9px 4px #ffca05;
  }

  ${props => pageStyle(props)}

  ${props => props.theme.breakpoints.up("desktop")} {
    ${ArticleImageContainer} {
      margin-inline: -88px;
    }
  }

  ${Heading1}, ${Heading2}, ${Heading3}, ${Heading4} {
    text-shadow: 2px 2px #ffca05;

    & * {
      text-shadow: initial;
    }
  }

  ${ArticleImageDescriptionContainer} {
    line-height: 16px;
  }
`;

export const CommentAndReaction = styled.div`
  grid-area: comment;

  margin-block-start: 52px;

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 335px;
  }
`;

export const Social = styled.div`
  grid-area: social;

  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;

  > *:empty {
    margin: 0;
    padding: 0;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 335px;
  }
`;

export const SocialMobile = styled.div`
  display: none;

  margin-block-start: 52px;

  ${props => props.theme.breakpoints.only("mobile")} {
    display: block;
  }
`;

export const SocialDesktop = styled.div`
  display: none;

  margin-block-start: 52px;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: block;
  }
`;

export const MoreOnThis = styled.div`
  grid-area: more-on-this;

  ${props => props.theme.breakpoints.only("mobile")} {
    margin-block-start: 32px;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block: 32px;
  }
`;

export const AuthorDetail = styled.div`
  grid-area: author-detail;

  margin-block-start: 32px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 20px;
  }
`;

export const Topic = styled.div`
  grid-area: topic;

  margin-block-start: 20px;
  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 16px;
  }
`;

export const HeaderAdSlot = styled(ArticleHeaderAdSlot)`
  grid-area: header-ad;

  display: none;

  ${props => props.theme.breakpoints.up("desktop")} {
    display: block;
  }
`;

export const ContentFooterAdSlot = styled.div`
  grid-area: content-footer-ad;
`;

export const FooterAdSlot = styled.div`
  grid-area: footer-ad;

  display: none;

  margin-block-start: 20px;

  ${props => props.theme.breakpoints.down("desktop")} {
    display: block;
  }

  ${props => props.theme.breakpoints.only("mobile")} {
    ${setFullViewportWidthWithMaxPageWidthStyles()}
  }
`;

export const SpeechPlayer = styled.div`
  grid-area: speech-player;
`;
